<template>
  <Teleport to="body" :disabled="!appendToBody">
    <transition name="modal">
      <div
        v-if="open"
        class="modal-mask standard-modal"
        :class="{ 'standard-modal--keyboard-aware': keyboardAware }"
        role="dialog"
        aria-modal="true"
        :aria-labelledby="titleId"
        @click.self="close"
      >
        <div class="modal-safari-bleed" aria-hidden="true"></div>
        <div class="modal-safari-tint" aria-hidden="true"></div>
        <div
          class="modal-wrapper"
          :style="wrapperStyle"
          @click.self="close"
        >
          <div class="modal-container" :class="sizeClass">
            <div class="modal-header">
              <i
                class="close icon"
                role="button"
                tabindex="0"
                :aria-label="'Close ' + title"
                @click="close"
                @keydown.enter.prevent="close"
                @keydown.space.prevent="close"
              ></i>
              <h3 :id="titleId">{{ title }}</h3>
            </div>

            <div class="ui divider"></div>

            <div class="modal-body content" :class="{ scrolling: scrolling }">
              <slot />
            </div>

            <div v-if="hasFooterSlot" class="modal-footer">
              <slot name="footer" />
            </div>
          </div>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<script>
export default {
  name: 'StandardModal',
  props: {
    open: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      required: true
    },
    titleId: {
      type: String,
      default: 'standard-modal-title'
    },
    size: {
      type: String,
      default: '',
      validator: function (value) {
        return ['', 'small', 'medium', 'compact'].indexOf(value) !== -1
      }
    },
    scrolling: {
      type: Boolean,
      default: true
    },
    /** Render above nested shells (e.g. full-page task modal) so parent CSS cannot stretch this dialog. */
    appendToBody: {
      type: Boolean,
      default: true
    },
    /**
     * Centre the dialog in the visible viewport (above the on-screen keyboard).
     * Backdrop stays full-screen; only the dialog wrapper tracks visualViewport.
     */
    keyboardAware: {
      type: Boolean,
      default: false
    }
  },
  data: function () {
    return {
      viewportTop: 0,
      viewportHeight: 0
    }
  },
  computed: {
    sizeClass: function () {
      return this.size
    },
    wrapperStyle: function () {
      if (!this.keyboardAware || !this.open || !this.viewportHeight) return null
      return {
        top: this.viewportTop + 'px',
        height: this.viewportHeight + 'px'
      }
    },
    hasFooterSlot: function () {
      const slot = this.$slots && (this.$slots.footer || this.$slots['footer'])
      if (!slot) return false
      if (typeof slot === 'function') {
        try {
          const nodes = slot()
          return Array.isArray(nodes) ? nodes.some(Boolean) : !!nodes
        } catch (e) {
          return true
        }
      }
      return true
    }
  },
  watch: {
    open: function (isOpen) {
      this.syncDimmedRoot(isOpen)
      if (isOpen && this.keyboardAware) {
        this.bindViewport()
        this.syncViewport()
      } else {
        this.unbindViewport()
        this.viewportTop = 0
        this.viewportHeight = 0
      }
    }
  },
  mounted () {
    document.addEventListener('keydown', this.onKeydown)
    if (this.open) {
      this.syncDimmedRoot(true)
      if (this.keyboardAware) {
        this.bindViewport()
        this.syncViewport()
      }
    }
  },
  beforeUnmount () {
    document.removeEventListener('keydown', this.onKeydown)
    this.syncDimmedRoot(false)
    this.unbindViewport()
  },
  beforeUnmount () {
    // Vue 2 compat
    document.removeEventListener('keydown', this.onKeydown)
    this.syncDimmedRoot(false)
    this.unbindViewport()
  },
  methods: {
    close () {
      this.$emit('close')
    },
    /**
     * Toggle root class so CSS paints a solid black dimmed backdrop (Safari-safe).
     */
    syncDimmedRoot (isOpen) {
      if (typeof document === 'undefined') return
      const root = document.documentElement
      const body = document.body
      if (isOpen) {
        root.classList.add('dimmed-modal-open')
        if (body) body.classList.add('dimmed-modal-open')
      } else {
        root.classList.remove('dimmed-modal-open')
        if (body) body.classList.remove('dimmed-modal-open')
      }
    },
    onKeydown (event) {
      if (this.open && event.key === 'Escape') this.close()
    },
    bindViewport () {
      if (typeof window === 'undefined' || !window.visualViewport) return
      window.visualViewport.addEventListener('resize', this.syncViewport)
      window.visualViewport.addEventListener('scroll', this.syncViewport)
      window.addEventListener('resize', this.syncViewport)
    },
    unbindViewport () {
      if (typeof window === 'undefined') return
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', this.syncViewport)
        window.visualViewport.removeEventListener('scroll', this.syncViewport)
      }
      window.removeEventListener('resize', this.syncViewport)
    },
    syncViewport () {
      if (!this.keyboardAware || !this.open) return
      const vv = window.visualViewport
      if (!vv) {
        this.viewportTop = 0
        this.viewportHeight = window.innerHeight || 0
        return
      }
      this.viewportTop = vv.offsetTop || 0
      this.viewportHeight = vv.height || window.innerHeight || 0
    }
  }
}
</script>
